import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

const STORAGE_KEY = 'cz:admin_company_id'
const STORAGE_NAME = 'cz:admin_company_name'

interface ImpersonationContextType {
  /** ID da empresa que o Super Admin está visualizando, ou null */
  viewingCompanyId: string | null
  viewingCompanyName: string | null
  /** Indica se o usuário atual é platform admin (carregado de RPC) */
  isPlatformAdmin: boolean
  isReady: boolean
  enterAsCompany: (companyId: string, companyName: string) => void
  exitImpersonation: () => void
}

const Ctx = createContext<ImpersonationContextType | null>(null)

export function useAdminImpersonation() {
  const c = useContext(Ctx)
  if (!c) throw new Error('useAdminImpersonation must be used within AdminImpersonationProvider')
  return c
}

/**
 * Lê o impersonation atual SEM precisar do contexto (útil em hooks de baixo nível).
 * Retorna null se não houver.
 */
export function readImpersonationCompanyId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY) || null
  } catch {
    return null
  }
}

export function AdminImpersonationProvider({ children }: { children: React.ReactNode }) {
  const [viewingCompanyId, setViewingCompanyId] = useState<string | null>(() => {
    try { return localStorage.getItem(STORAGE_KEY) } catch { return null }
  })
  const [viewingCompanyName, setViewingCompanyName] = useState<string | null>(() => {
    try { return localStorage.getItem(STORAGE_NAME) } catch { return null }
  })
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false)
  const [isReady, setIsReady] = useState(false)

  // Verifica se usuário atual é platform admin
  useEffect(() => {
    let cancelled = false
    async function check() {
      try {
        const { data } = await supabase.rpc('is_platform_admin')
        if (!cancelled) setIsPlatformAdmin(data === true)
      } catch {
        if (!cancelled) setIsPlatformAdmin(false)
      } finally {
        if (!cancelled) setIsReady(true)
      }
    }
    check()
    // Re-checa em mudança de auth
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      check()
    })
    return () => { cancelled = true; sub.subscription.unsubscribe() }
  }, [])

  // Se o usuário deixar de ser platform admin, limpa qualquer impersonation
  useEffect(() => {
    if (isReady && !isPlatformAdmin && viewingCompanyId) {
      try {
        localStorage.removeItem(STORAGE_KEY)
        localStorage.removeItem(STORAGE_NAME)
      } catch { /* noop */ }
      setViewingCompanyId(null)
      setViewingCompanyName(null)
    }
  }, [isReady, isPlatformAdmin, viewingCompanyId])

  const enterAsCompany = useCallback((companyId: string, companyName: string) => {
    try {
      localStorage.setItem(STORAGE_KEY, companyId)
      localStorage.setItem(STORAGE_NAME, companyName)
      // Limpa obra ativa para forçar seleção dentro da empresa visualizada
      localStorage.removeItem('cpro_ativa')
      localStorage.removeItem('cz:obra_id')
      localStorage.setItem('cz:company_id', companyId)
    } catch { /* noop */ }
    setViewingCompanyId(companyId)
    setViewingCompanyName(companyName)
    // Força reload completo para que o AuthContext recarregue obras/empresa
    // usando o ID efetivo (impersonado) durante a inicialização
    window.location.assign('/')
  }, [])

  const exitImpersonation = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(STORAGE_NAME)
      localStorage.removeItem('cpro_ativa')
      localStorage.removeItem('cz:obra_id')
      localStorage.removeItem('cz:company_id')
    } catch { /* noop */ }
    setViewingCompanyId(null)
    setViewingCompanyName(null)
    // Reload para reinicializar contexto com a empresa real do Super Admin
    window.location.assign('/superadmin')
  }, [])

  return (
    <Ctx.Provider value={{
      viewingCompanyId,
      viewingCompanyName,
      isPlatformAdmin,
      isReady,
      enterAsCompany,
      exitImpersonation,
    }}>
      {children}
    </Ctx.Provider>
  )
}
